import { useState } from "react";
import { Link } from "react-router-dom";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import type { GlobalReportType } from "../../type/globalReportTypes";
import css from "./GlobalReportCardGrid.module.css";

interface GlobalReportCardGridProps {
  types: GlobalReportType[];
}

function groupByCategory(
  types: GlobalReportType[],
): Map<string | undefined, GlobalReportType[]> {
  const map = new Map<string | undefined, GlobalReportType[]>();
  for (const type of types) {
    const key = type.category ?? undefined;
    const list = map.get(key) ?? [];
    list.push(type);
    map.set(key, list);
  }
  return map;
}

const CATEGORY_ORDER = ["Geographic", "Sector", "Global Dynamics"];

export function GlobalReportCardGrid({ types }: GlobalReportCardGridProps) {
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());

  const byCategory = groupByCategory(types);
  const categories = CATEGORY_ORDER.filter((c) => byCategory.has(c));
  const uncategorized = byCategory.get(undefined) ?? [];

  const toggle = (category: string) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(category)) next.delete(category);
      else next.add(category);
      return next;
    });
  };

  return (
    <div className={css.cardGrid}>
      {categories.map((category) => {
        const isCollapsed = collapsed.has(category);
        const categoryTypes = byCategory.get(category)!;
        return (
          <div key={category} className={css.categorySection}>
            <button
              type="button"
              className={css.categoryHeader}
              onClick={() => toggle(category)}
              aria-expanded={!isCollapsed}
            >
              <span className={css.categoryHeaderText}>{category}</span>
              {isCollapsed ? (
                <ExpandMore className={css.chevron} />
              ) : (
                <ExpandLess className={css.chevron} />
              )}
            </button>
            {!isCollapsed && (
              <div className={css.categoryCards}>
                {categoryTypes.map((type) => (
                  <Link
                    key={type.slug}
                    to={`/global-research/${type.slug}`}
                    className={css.card}
                  >
                    <h3 className={css.cardTitle}>{type.title}</h3>
                    {type.description && (
                      <p className={css.cardDescription}>{type.description}</p>
                    )}
                  </Link>
                ))}
              </div>
            )}
          </div>
        );
      })}
      {uncategorized.length > 0 && (
        <div className={css.categorySection}>
          <button
            type="button"
            className={css.categoryHeader}
            onClick={() => toggle("Other")}
            aria-expanded={!collapsed.has("Other")}
          >
            <span className={css.categoryHeaderText}>Other</span>
            {collapsed.has("Other") ? (
              <ExpandMore className={css.chevron} />
            ) : (
              <ExpandLess className={css.chevron} />
            )}
          </button>
          {!collapsed.has("Other") && (
            <div className={css.categoryCards}>
              {uncategorized.map((type) => (
                <Link
                  key={type.slug}
                  to={`/global-research/${type.slug}`}
                  className={css.card}
                >
                  <h3 className={css.cardTitle}>{type.title}</h3>
                  {type.description && (
                    <p className={css.cardDescription}>{type.description}</p>
                  )}
                </Link>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
